<template>
  <div class="q-mx-auto q-py-sm" style="max-width: 900px">
    <q-card flat bordered>
      <q-select
        outline
        emit-value
        map-options
        multiple
        clearable
        use-input
        v-model="selectedDimensionsModel"
        :options="optionDimensions"
        @update:model-value="onClickDimension()"
        option-value="name"
        option-label="verbose_name"
        label="Select Dimension(s)"
        @filter="filterQSelectDimensions"
        class="q-ma-md"
        :menu-absolute="true"
      />

      <div v-if="dimensions.length" class="q-ma-md">
        <q-badge
          :color="$q.dark.mode ? 'grey-9' : 'primary'"
          class="q-ma-sm q-pa-sm text-bold"
          v-for="dimName in selectedDimensionsModel"
          :key="dimName"
        >
          {{ dimensions.filter((d) => d.name === dimName)[0].verbose_name }}
          <q-btn
            round
            size="xs"
            class="q-ml-sm q-pa-none"
            icon="fa fa-times"
            @click="removeDimension(dimName)"
          ></q-btn>
        </q-badge>
      </div>

      <div flat class="q-ma-md">
        <div
          class="text-body2 q-py-xs"
          :class="$q.dark.mode ? '' : 'text-primary '"
        >
          Fiscal Year :
        </div>

        <q-range
          v-if="fiscalYears.length"
          class="q-px-md"
          v-model="fiscalYearKeys"
          :color="$q.dark.mode ? 'grey-7' : 'primary'"
          :min="0"
          :max="fiscalYears.length - 1"
          markers
          :marker-labels="
            fiscalYears.map((fy, idx) => {
              return {
                value: idx,
                label: fy,
              };
            })
          "
          label-always
          switch-label-side
          switch-marker-labels-side
          @update:model-value="onClickFiscalYear()"
          :left-label-value="fiscalYearKeys.min"
          :right-label-value="fiscalYearKeys.max"
        />
      </div>

      <q-tabs
        v-model="tabDisplay"
        dense
        :class="$q.dark.mode ? 'bg-grey-10' : 'text-grey-9 bg-grey-4'"
        active-color="white"
        :active-bg-color="$q.dark.mode ? 'grey-9' : 'blue-4'"
        :indicator-color="$q.dark.mode ? 'grey-7' : 'blue-5'"
        align="justify"
        narrow-indicator
      >
        <q-tab name="graph" label="Graph View" no-caps></q-tab>
        <q-tab name="tabular" label="Tabular View" no-caps></q-tab>
      </q-tabs>

      <q-tab-panels v-model="tabDisplay" animated>
        <q-tab-panel class="q-pa-md" name="graph">
          <div v-if="dimensions.length">
            <q-card
              v-for="dimensionName in selectedDimensionsModel"
              :key="dimensionName"
              bordered
              flat
              square
            >
              <p
                class="text-body1 text-bold q-ma-md"
                :class="$q.dark.mode ? '' : 'text-primary '"
              >
                {{
                  dimensions.filter((d) => d.name === dimensionName)[0]
                    .verbose_name
                }}
              </p>

              <div id="chart">
                <apexcharts
                  v-if="
                    stockReportsByDimension &&
                    stockReportsByDimension[dimensionName] &&
                    stockReportsByDimension[dimensionName]['time_period'] &&
                    stockReportsByDimension[dimensionName]['data']
                  "
                  type="line"
                  height="350"
                  :options="{
                    chart: {
                      height: 350,
                      type: 'line',
                      zoom: { enabled: false },
                    },
                    dataLabels: {
                      enabled: true,
                    },
                    fill: {
                      opacity: 1,
                    },
                    tooltip: $q.dark.mode
                      ? { theme: 'dark' }
                      : { theme: 'light' },
                    stroke: { curve: 'smooth', width: 2 },
                    // grid: {
                    //   row: { colors: ['#f3f3f3', 'transparent'], opacity: 0.5 },
                    // },
                    xaxis: {
                      categories: stockReportsByDimension[dimensionName][
                        'time_period'
                      ].map((t) => t.name),
                      labels: {
                        style: {
                          colors: $q.dark.mode ? '#fff' : '#000',
                        },
                      },
                    },
                    yaxis: {
                      labels: {
                        style: {
                          colors: $q.dark.mode ? '#fff' : '#000',
                        },
                      },
                    },
                  }"
                  :series="[
                    {
                      name: dimensionName,
                      data: stockReportsByDimension[dimensionName]['data'],
                    },
                  ]"
                ></apexcharts>
              </div>
            </q-card>
          </div>
        </q-tab-panel>

        <q-tab-panel class="q-pa-none" name="tabular">
          <div v-if="dimensions.length">
            <q-card
              v-for="dimensionName in selectedDimensionsModel"
              :key="dimensionName"
              bordered
              flat
              square
            >
              <p
                class="text-body1 text-bold q-ma-md"
                :class="$q.dark.mode ? '' : 'text-primary '"
              >
                {{
                  dimensions.filter((d) => d.name === dimensionName)[0]
                    .verbose_name
                }}
              </p>
              <q-separator />

              <q-table
                v-if="
                  stockReportsByDimension &&
                  stockReportsByDimension[dimensionName] &&
                  stockReportsByDimension[dimensionName]['time_period'] &&
                  stockReportsByDimension[dimensionName]['data_table']
                "
                :rows="stockReportsByDimension[dimensionName]['data_table']"
                :columns="stockReportsByDimension[dimensionName]['time_period']"
                hide-bottom
                separator="cell"
                dense
                :class="
                  $q.dark.mode
                    ? 'table-sticky-header-column-dark'
                    : 'table-sticky-header-column'
                "
              />
            </q-card>
          </div>
        </q-tab-panel>
      </q-tab-panels>
    </q-card>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from "vue";
import { useQuasar } from "quasar";
import { useStore } from "vuex";
import useCore from "src/use/core";
import apexcharts from "vue3-apexcharts";

const props = defineProps({
  theme: {
    type: Boolean,
    default: false,
  },
  symbol: {
    type: String,
    required: true,
  },
});

const { stockSearch, sectorDimension, stockReport } = useCore();
const stock = ref(null);
const $q = useQuasar();
const store = useStore();

const fiscalYears = ref([]);
const fiscalYearKeys = ref({ min: 3, max: 5 });
const tabDisplay = ref("graph");

const dimensions = ref([]);
const optionDimensions = ref([]);
const selectedDimensionsModel = ref(["eps", "net_worth", "net_profit", "roe"]);

const stockReports = ref([]);
const stockReportsByDimension = ref({});

function loadStockReports(symbol) {
  stockSearch(symbol).then((data) => {
    stock.value = data;

    sectorDimension(stock.value.sector).then((res) => {
      dimensions.value = res.data;
      optionDimensions.value = res.data;

      stockReport(stock.value.id).then((res) => {
        stockReports.value = res.data;

        fiscalYears.value = [
          ...new Set(stockReports.value.map((item) => item.fiscal_year)),
        ].sort((a, b) => {
          a = a.split("/");
          b = b.split("/");
          return (
            parseInt(a[0]) + parseInt(a[1]) - parseInt(b[0]) - parseInt(b[1])
          );
        });

        fiscalYearKeys.value = {
          min: fiscalYears.value.length - 3,
          max: fiscalYears.value.length - 1,
        };

        updateReportChartTable();
      });
    });
  });
}

function updateReportChartTable() {
  stockReportsByDimension.value = {};
  optionDimensions.value.forEach((dim) => {
    if (!stockReportsByDimension.value[dim.name]) {
      stockReportsByDimension.value[dim.name] = {};
    }

    stockReportsByDimension.value[dim.name]["verbose_name"] = dim.verbose_name;

    stockReportsByDimension.value[dim.name]["time_period"] = stockReports.value
      .filter((sr) => {
        return fiscalYears.value
          .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
          .includes(sr.fiscal_year);
      })
      .map((sr) => {
        return {
          name: sr["quarter"] + "-" + sr["fiscal_year"],
          label: sr["quarter"] + "-" + sr["fiscal_year"],
          field: sr["quarter"] + "-" + sr["fiscal_year"],
        };
      })
      .reverse();

    stockReportsByDimension.value[dim.name]["data"] = stockReports.value
      .filter((sr) => {
        return fiscalYears.value
          .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
          .includes(sr.fiscal_year);
      })
      .map((sr) => (sr[dim.name] ? parseFloat(sr[dim.name]).toFixed(2) : null))
      .reverse();

    stockReportsByDimension.value[dim.name]["data_table"] = [
      stockReports.value
        .filter((sr) => {
          return fiscalYears.value
            .slice(fiscalYearKeys.value.min, fiscalYearKeys.value.max + 1)
            .includes(sr.fiscal_year);
        })
        .reverse()
        .reduce((acc, curr) => {
          var qtrfykey = curr["quarter"] + "-" + curr["fiscal_year"];
          if (!acc[qtrfykey]) {
            acc[qtrfykey] = curr[dim.name]
              ? parseFloat(curr[dim.name]).toFixed(2)
              : "N/A";
          }
          return acc;
        }, {}),
    ];
  });
}

function onClickDimension() {
  updateReportChartTable();
}

function onClickFiscalYear() {
  updateReportChartTable();
}

function filterQSelectDimensions(val, update) {
  if (val === "") {
    update(() => {
      optionDimensions.value = dimensions.value;
    });
    return;
  }

  update(() => {
    const needle = val.toLowerCase();
    optionDimensions.value = dimensions.value.filter((v) => {
      return v.name.toLowerCase().indexOf(needle) > -1;
    });
  });
}

function removeDimension(clickedDimName) {
  selectedDimensionsModel.value = selectedDimensionsModel.value.filter(
    (dimName) => dimName !== clickedDimName
  );
  onClickDimension();
}

onMounted(() => {
  loadStockReports(props.symbol);
});

watch(props, (newProps, oldProps) => {
  loadStockReports(newProps.symbol);
});
</script>
